import { CATEGORIES, CORE, type TalkWord, type WordType } from './vocabulary';

export const CORE_ID = 'core';
export const MAX_PAGE_WORDS = 60;
export const MAX_PAGES = 40;

export type OwnWord = { id: string; word: string; picture: string; type: WordType };
export type Page = { id: string; label: string; picture: string; words: string[] };
export type Layout = { version: 2; core: string[]; pages: Page[]; own: OwnWord[]; hidden: string[] };

type Legacy = { added?: { id: string; category: string; word: string; picture: string; type: WordType }[]; hidden?: string[] };

const BUILT_IN = new Map<string, TalkWord>([...CORE, ...CATEGORIES.flatMap((c) => c.words)].map((w) => [w.id, w]));

export function fromData(data: unknown): Layout {
  const d = (data ?? {}) as Partial<Layout> & Legacy;
  if (d.version === 2 && Array.isArray(d.core) && Array.isArray(d.pages)) {
    return {
      version: 2,
      core: d.core.filter((x): x is string => typeof x === 'string'),
      pages: d.pages.map((p) => ({ id: p.id, label: p.label, picture: p.picture ?? '', words: Array.isArray(p.words) ? p.words : [] })),
      own: Array.isArray(d.own) ? d.own : [],
      hidden: Array.isArray(d.hidden) ? d.hidden : [],
    };
  }
  const added = d.added ?? [];
  return {
    version: 2,
    core: CORE.map((w) => w.id),
    pages: CATEGORIES.map((c) => ({
      id: c.id,
      label: c.label,
      picture: c.picture,
      words: [...c.words.map((w) => w.id), ...added.filter((a) => a.category === c.id).map((a) => a.id)],
    })),
    own: added.map((a) => ({ id: a.id, word: a.word, picture: a.picture, type: a.type })),
    hidden: d.hidden ?? [],
  };
}

export function wordOf(layout: Layout, id: string): TalkWord | undefined {
  return layout.own.find((o) => o.id === id) ?? BUILT_IN.get(id);
}

export const isOwn = (id: string) => id.startsWith('own:');

export function containerOf(layout: Layout, id: string): string | null {
  if (layout.core.includes(id)) return CORE_ID;
  return layout.pages.find((p) => p.words.includes(id))?.id ?? null;
}

export function idsIn(layout: Layout, container: string): string[] {
  return container === CORE_ID ? layout.core : layout.pages.find((p) => p.id === container)?.words ?? [];
}

function withIds(layout: Layout, container: string, ids: string[]): Layout {
  if (container === CORE_ID) return { ...layout, core: ids };
  return { ...layout, pages: layout.pages.map((p) => (p.id === container ? { ...p, words: ids } : p)) };
}

export function isFull(layout: Layout, container: string) {
  return container !== CORE_ID && idsIn(layout, container).length >= MAX_PAGE_WORDS;
}

export function place(layout: Layout, id: string, to: string, index?: number): Layout {
  const from = containerOf(layout, id);
  let next = layout;
  if (from) next = withIds(next, from, idsIn(next, from).filter((x) => x !== id));
  const target = [...idsIn(next, to)];
  target.splice(index === undefined ? target.length : Math.max(0, Math.min(index, target.length)), 0, id);
  return withIds(next, to, target);
}

export function reorder(layout: Layout, container: string, from: number, to: number): Layout {
  const ids = [...idsIn(layout, container)];
  const [moved] = ids.splice(from, 1);
  ids.splice(to, 0, moved);
  return withIds(layout, container, ids);
}

export function addOwn(layout: Layout, word: OwnWord, to: string): Layout {
  return place({ ...layout, own: [...layout.own, word] }, word.id, to);
}

export function updateOwn(layout: Layout, id: string, patch: Partial<OwnWord>): Layout {
  return { ...layout, own: layout.own.map((o) => (o.id === id ? { ...o, ...patch } : o)) };
}

export function removeWord(layout: Layout, id: string): Layout {
  const from = containerOf(layout, id);
  const next = from ? withIds(layout, from, idsIn(layout, from).filter((x) => x !== id)) : layout;
  return { ...next, own: next.own.filter((o) => o.id !== id), hidden: next.hidden.filter((h) => h !== id) };
}

export function setHidden(layout: Layout, id: string, hidden: boolean): Layout {
  const rest = layout.hidden.filter((h) => h !== id);
  return { ...layout, hidden: hidden ? [...rest, id] : rest };
}

export function addPage(layout: Layout, label: string, picture: string): { layout: Layout; id: string } {
  const id = `page:${Date.now()}`;
  return { id, layout: { ...layout, pages: [...layout.pages, { id, label, picture, words: [] }] } };
}

export function updatePage(layout: Layout, id: string, patch: Partial<Pick<Page, 'label' | 'picture'>>): Layout {
  return { ...layout, pages: layout.pages.map((p) => (p.id === id ? { ...p, ...patch } : p)) };
}

export function removePage(layout: Layout, id: string): Layout {
  const page = layout.pages.find((p) => p.id === id);
  if (!page) return layout;
  const gone = new Set(page.words);
  return {
    ...layout,
    pages: layout.pages.filter((p) => p.id !== id),
    own: layout.own.filter((o) => !gone.has(o.id)),
    hidden: layout.hidden.filter((h) => !gone.has(h)),
  };
}

export function reorderPages(layout: Layout, from: number, to: number): Layout {
  const pages = [...layout.pages];
  const [moved] = pages.splice(from, 1);
  pages.splice(to, 0, moved);
  return { ...layout, pages };
}

export function allWords(layout: Layout): TalkWord[] {
  return [...layout.core, ...layout.pages.flatMap((p) => p.words)].map((id) => wordOf(layout, id)).filter((w): w is TalkWord => !!w);
}
